export const FAVICON_THEME_COLOR = `#ffb700`

export interface FaviconConfigOptions {
  baseUrl?: string
  version?: string
}

export interface FaviconUrls {
  appleTouchIcon: string
  favicon96: string
  faviconIco: string
  faviconSvg: string
  manifest: string
  webAppManifest192: string
  webAppManifest512: string
}

export interface FaviconWebManifest {
  name: string
  short_name: string
  display: `standalone`
  background_color: string
  theme_color: string
  icons: Array<{
    src: string
    sizes: string
    type: `image/png`
  }>
}

const addVersion = (url: string, version?: string): string => {
  const normalizedVersion = version?.trim()

  if (!normalizedVersion) {
    return url
  }

  const separator = url.includes(`?`) ? `&` : `?`
  return `${url}${separator}v=${encodeURIComponent(normalizedVersion)}`
}

const normalizeBaseUrl = (baseUrl?: string): string => {
  const normalizedBaseUrl = baseUrl?.trim().replace(/\/+$/, ``)

  if (!normalizedBaseUrl) {
    throw new Error(`GATSBY_FAVICON_URL is required to build favicon URLs.`)
  }

  let parsedUrl: URL

  try {
    parsedUrl = new URL(normalizedBaseUrl)
  } catch {
    throw new Error(`GATSBY_FAVICON_URL must be an absolute URL.`)
  }

  if (parsedUrl.protocol !== `http:` && parsedUrl.protocol !== `https:`) {
    throw new Error(`GATSBY_FAVICON_URL must use HTTP or HTTPS.`)
  }

  return normalizedBaseUrl
}

export const getFaviconUrls = ({
  baseUrl,
  version,
}: FaviconConfigOptions): FaviconUrls => {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl)

  return {
    appleTouchIcon: addVersion(
      `${normalizedBaseUrl}/apple-touch-icon.png`,
      version
    ),
    favicon96: addVersion(`${normalizedBaseUrl}/favicon-96x96.png`, version),
    faviconIco: addVersion(`${normalizedBaseUrl}/favicon.ico`, version),
    faviconSvg: addVersion(`${normalizedBaseUrl}/favicon.svg`, version),
    manifest: addVersion(`/site.webmanifest`, version),
    webAppManifest192: addVersion(
      `${normalizedBaseUrl}/web-app-manifest-192x192.png`,
      version
    ),
    webAppManifest512: addVersion(
      `${normalizedBaseUrl}/web-app-manifest-512x512.png`,
      version
    ),
  }
}

export const getFaviconWebManifest = (
  options: FaviconConfigOptions
): FaviconWebManifest => {
  const urls = getFaviconUrls(options)

  return {
    name: `Easy8`,
    short_name: `Easy8`,
    display: `standalone`,
    background_color: FAVICON_THEME_COLOR,
    theme_color: FAVICON_THEME_COLOR,
    icons: [
      {
        src: urls.webAppManifest192,
        sizes: `192x192`,
        type: `image/png`,
      },
      {
        src: urls.webAppManifest512,
        sizes: `512x512`,
        type: `image/png`,
      },
    ],
  }
}
